import type { BezugKandidat } from "./bezugKandidaten";
import { aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * Die `#`-Mechanik der Notizblöcke (PO 08.09.2026) — Token lesen und Bezug
 * einsetzen, beides auf der echten Selektion.
 *
 * Bewusst OHNE React: im `contentEditable` ist das DOM die Quelle der Wahrheit
 * (siehe Kopf von {@link ../notizbloecke/NotizBlock}), und genau diese
 * Selektions-Arithmetik ist der Teil, der schiefgehen kann. Als freie
 * Funktionen ist sie im jsdom prüfbar, ohne eine Komponente zu montieren.
 */

/** Womit sich eine Auswahl im Notiztext öffnet. */
export type Ausloeser = "@" | "#";

/**
 * Das Token unmittelbar vor dem Cursor — dieselbe Regel wie in
 * `notes/mentions.ts` (`activeToken`), nur auf der Selektion statt auf einem
 * Klartext-Index: im contentEditable gibt es keinen numerischen Caret über den
 * ganzen Inhalt.
 *
 * `""` heißt „gerade den Auslöser getippt" (alles steht zur Wahl), `null` heißt
 * „hier ist kein Token" — die beiden dürfen nicht zusammenfallen.
 */
export function tokenVorCursor(sel: Selection | null, ausloeser: Ausloeser): string | null {
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
    const knoten = sel.anchorNode;
    if (!knoten || knoten.nodeType !== Node.TEXT_NODE) return null;
    const bisCursor = (knoten.textContent ?? "").slice(0, sel.anchorOffset);
    const start = bisCursor.lastIndexOf(ausloeser);
    if (start < 0) return null;
    const zwischen = bisCursor.slice(start + 1);
    // Ein Leerzeichen beendet das Token — „# " ist keine Auswahl mehr.
    return /\s/.test(zwischen) ? null : zwischen;
}

/** Das `#`-Token vor dem Cursor. */
export function hashTokenVorCursor(sel: Selection | null): string | null {
    return tokenVorCursor(sel, "#");
}

/** Das `@`-Token vor dem Cursor — die Personen-Erwähnung. */
export function mentionTokenVorCursor(sel: Selection | null): string | null {
    return tokenVorCursor(sel, "@");
}

/** Welche Auswahl gerade offen sein darf. */
export type Erlaubt = { hash?: boolean; at?: boolean };

/** Ein angefangenes Token mit dem Auslöser, der es geöffnet hat. */
export type AktivesToken = { ausloeser: Ausloeser; query: string };

/**
 * Welches Token steht gerade vor dem Cursor — `#` oder `@`?
 *
 * Beide können im selben Satz stehen („#Vertrag für @mm"). Dann gewinnt das
 * SPÄTERE: es ist das, das gerade getippt wird. Weil beide Suchen am selben
 * Cursor enden, ist das kürzere Token zwangsläufig das spätere — bei
 * Gleichstand entscheidet `@`, weil ein `#` in einer Mailadresse nicht
 * vorkommt, ein `@` in einem Betreff dagegen schon.
 *
 * `erlaubt` blendet aus, wofür es gar kein Angebot gibt: ohne Kandidaten
 * bleibt `#` ein gewöhnliches Zeichen, ohne Nutzerliste `@`.
 */
export function aktivesToken(
    sel: Selection | null,
    erlaubt: Erlaubt = { hash: true, at: true },
): AktivesToken | null {
    const hash = erlaubt.hash ? tokenVorCursor(sel, "#") : null;
    const at = erlaubt.at ? tokenVorCursor(sel, "@") : null;
    if (hash === null && at === null) return null;
    if (at === null) return { ausloeser: "#", query: hash as string };
    if (hash === null) return { ausloeser: "@", query: at };
    return at.length <= hash.length
        ? { ausloeser: "@", query: at }
        : { ausloeser: "#", query: hash };
}

/**
 * Das getippte Token durch ein Element ersetzen und den Cursor dahintersetzen.
 * Gemeinsame Mechanik von `#` (Bezug) und `@` (Erwähnung) — sie unterscheiden
 * sich nur darin, WAS sie einsetzen.
 *
 * Gibt `false` zurück, wenn an der Cursorposition kein Token steht. Dann
 * bleibt der Text unangetastet: ein Fehlgriff darf nichts wegfräsen.
 */
function tokenErsetzen(
    sel: Selection | null,
    ausloeser: Ausloeser,
    bauen: () => HTMLElement,
): boolean {
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    const knoten = range.startContainer;
    if (knoten.nodeType !== Node.TEXT_NODE) return false;
    const text = knoten.textContent ?? "";
    const start = text.slice(0, range.startOffset).lastIndexOf(ausloeser);
    if (start < 0) return false;

    const treffer = aktivesDokument().createRange();
    treffer.setStart(knoten, start);
    treffer.setEnd(knoten, range.startOffset);
    treffer.deleteContents();

    const el = bauen();
    // Das Eingesetzte ist EIN Ding, kein Text (PO 09.09.2026): ohne das schrieb
    // der Nutzer hinein und der getippte Satz nahm seine Farbe an.
    // `contenteditable` steht nicht in den erlaubten Attributen von
    // `reinigeBlockHtml` und fällt beim Speichern weg — gespeichert wird der
    // schlanke Marker, gesetzt wird das Attribut beim Anzeigen neu.
    el.setAttribute("contenteditable", "false");
    treffer.insertNode(el);

    /*
     * Cursor dahinter, damit man direkt weiterschreibt.
     *
     * Der Textknoten ist der eigentliche Kniff: `setStartAfter(el)` allein
     * setzt den Caret auf eine ELEMENTGRENZE im Elternknoten, und die schlagen
     * Browser dem vorangehenden Inline-Element zu — der nächste Anschlag
     * landete IM Chip. Ein geschütztes Leerzeichen gibt dem Caret einen
     * eigenen Platz außerhalb.
     */
    const luecke = document.createTextNode("\u00A0");
    el.after(luecke);

    const danach = aktivesDokument().createRange();
    danach.setStart(luecke, 1);
    danach.collapse(true);
    sel.removeAllRanges();
    sel.addRange(danach);
    return true;
}

/**
 * Den getippten `#token` durch die Darstellung des Bezugs ersetzen. Der Chip
 * trägt Art und Id als `data-`-Attribute, damit der Verweis den Text
 * überlebt (`reinigeBlockHtml` lässt genau diese beiden Attribute stehen);
 * maßgeblich bleibt aber die `refs`-Liste, die mitgespeichert wird — das
 * Markup ist Anzeige, nicht Wahrheit.
 */
export function bezugEinsetzen(sel: Selection | null, k: BezugKandidat): boolean {
    return tokenErsetzen(sel, "#", () => {
        const el = document.createElement("span");
        el.className = `nb-ref nb-art-${k.art}`;
        el.setAttribute("data-ref-art", k.art);
        el.setAttribute("data-ref-id", String(k.id));
        el.textContent = k.titel;
        return el;
    });
}

/**
 * Den getippten `@token` durch die Erwähnung ersetzen.
 *
 * Der Text bleibt sichtbar `@token` — bewusst, denn `resolveTypedMentions`
 * (`notes/mentions.ts`) löst Erwähnungen aus dem TEXT auf. So zählt eine
 * Erwähnung auch dann, wenn jemand sie von Hand tippt, statt sie zu wählen.
 * Die Klasse ist reine Auszeichnung; wer benachrichtigt wird, entscheidet die
 * `mentions`-Liste, die mitgespeichert wird.
 */
export function mentionEinsetzen(sel: Selection | null, token: string): boolean {
    return tokenErsetzen(sel, "@", () => {
        const el = document.createElement("span");
        el.className = "nb-mention";
        el.textContent = `@${token}`;
        return el;
    });
}
